/* Shared settings shell: left sidebar of tabs + right content panel.
   Used by both BuyerSettings and SellerSettings with role-specific tab lists. */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { PageHeader } from "./PortalShell";

export default function SettingsShell({ title, subtitle, tabs, banner = null, onTabChange = null }) {
  const location = useLocation();
  const initialFromHash = (location.hash || "").replace("#", "") || tabs[0]?.id;
  const [active, setActive] = useState(
    tabs.find(t => t.id === initialFromHash)?.id || tabs[0]?.id
  );
  // Sync to hash changes (so cross-page links like `/seller/settings#public-profile` work).
  useEffect(() => {
    const id = (location.hash || "").replace("#", "");
    if (id && tabs.find(t => t.id === id)) setActive(id);
  }, [location.hash, tabs]);
  const activeTab = tabs.find(t => t.id === active);

  return (
    <div data-testid="settings-shell">
      <PageHeader title={title} subtitle={subtitle} />
      {banner}
      <div className="p-8 flex flex-col lg:flex-row gap-6 max-w-[1400px]">
        {/* Left nav */}
        <aside className="lg:w-64 shrink-0">
          <nav className="sticky top-24 space-y-0.5">
            {tabs.map((t, idx) => {
              // Group separators via t.group
              const prev = tabs[idx - 1];
              const showGroup = t.group && prev?.group !== t.group;
              const on = active === t.id;
              const Icon = t.icon;
              return (
                <div key={t.id}>
                  {showGroup && (
                    <div className="text-[10px] mono uppercase tracking-widest text-foreground/40 px-3 mt-5 mb-2">{t.group}</div>
                  )}
                  <button
                    onClick={() => { setActive(t.id); onTabChange?.(t.id); }}
                    data-testid={`settings-tab-${t.id}`}
                    className={`w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[13.5px] transition-colors ${on ? "bg-foreground text-background" : "text-foreground/70 hover:bg-secondary hover:text-foreground"}`}
                  >
                    {Icon && <Icon className="w-4 h-4 shrink-0"/>}
                    <span className="flex-1 truncate">{t.label}</span>
                    {t.badge && <span className="text-[10px] mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-accent/20 text-foreground/70">{t.badge}</span>}
                  </button>
                </div>
              );
            })}
          </nav>
        </aside>

        {/* Right content */}
        <section className="flex-1 min-w-0 rounded-2xl border border-border bg-white p-7" data-testid={`settings-panel-${active}`}>
          {activeTab?.render?.()}
        </section>
      </div>
    </div>
  );
}

/* Reusable field wrapper + save bar.
   Optional `id` lets a settings panel deep-link directly to a specific row
   via URL query/hash, e.g. /settings?focus=offer-seller-arranged-shipping. */
export function FieldRow({ id, label, hint, children }) {
  return (
    <div
      id={id}
      className="grid md:grid-cols-[220px_1fr] gap-3 md:gap-6 py-4 border-b border-border/60 last:border-0 scroll-mt-24"
    >
      <div>
        <div className="text-[13.5px] font-medium">{label}</div>
        {hint && <div className="text-[11.5px] text-foreground/55 mt-0.5">{hint}</div>}
      </div>
      <div>{children}</div>
    </div>
  );
}

export function SectionHeader({ title, desc, action }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-4">
      <div>
        <h3 className="display text-xl tracking-tight">{title}</h3>
        {desc && <p className="text-[13px] text-foreground/60 mt-1 max-w-xl">{desc}</p>}
      </div>
      {action}
    </div>
  );
}

export function ComingSoon({ children }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-accent/15 text-foreground/70 border border-accent/30">
      {children || "Coming soon"}
    </span>
  );
}

/**
 * FocusScroller · Iter 159
 * Drop this inside any settings panel and it'll read `?focus=<id>` from the
 * URL, scroll the matching <FieldRow id=…> into view, and briefly highlight
 * it with an amber outline so the user spots the field that was deep-linked.
 *
 * Used e.g. by /seller/shipping → "Shipping settings" deep-linking into the
 * "Offer seller-arranged shipping" toggle inside Public profile.
 */
export function FocusScroller({ delay = 250 }) {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const id = params.get("focus");
    if (!id) return;
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      // Brief amber highlight pulse.
      const prev = el.style.boxShadow;
      el.style.transition = "box-shadow 1.2s ease-out";
      el.style.boxShadow  = "0 0 0 3px rgba(245,158,11,0.55)";
      setTimeout(() => { el.style.boxShadow = prev || ""; }, 2200);
    }, delay);
    return () => clearTimeout(t);
  }, [location.search, delay]);
  return null;
}
